import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AssessmentConfiguration, AssessmentToCompare, AWSOnPremisesSummary } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import {
    Box,
    Button, Checkbox, CheckboxProps, Container, Form, FormField, Input, InputProps, Modal, NonCancelableCustomEvent, Select, SelectProps, SpaceBetween,
    Spinner, Table, TableProps, Toggle, ToggleProps,
} from '@amzn/awsui-components-react';
import { FormatNumberOptions } from '@formatjs/intl';
import { useContext, useEffect, useState } from 'react';

import { FormAction } from '../../../common/form-builder/types';
import { constants } from '../../../common/Localization';
import { formatCurrencyAsString, formatPercentage } from '../../../common/LocalizationHelper';
import { isStringArray } from '../../../common/type-guards';
import { deepCopy } from '../../../common/util';
import { PortfolioContext } from '../../../contexts/PortfolioContext';
import { TcoContext } from '../../../contexts/TcoContext';
import AWSUIFormBuilder, { DistributeMetadataItem, EnumItem } from '../../common/AWSUIFormBuilder';
import { categories, Category, friendlyValueNames } from './CompareAssessmentHelper';

type Props = {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    onCreate: (newAssessment: CreateNewAssessment) => void;
    onEdit: (updatedAssessment: EditAssessment) => void;
    assessment?: EditAssessment;
    formAction: FormAction;
} & FlashContextInterface;

export interface EditAssessment {
    id: string;
    name: string;
    overrideAssumptions: boolean;
    copyAssessmentId: SelectProps.Option;
}
export interface CreateNewAssessment {
    name: string;
    useDefault: string;
    copyAssessmentId?: string;
}

const tcoService = new TcoService();

function AssessmentControlsModal({ assessment, formAction, onCreate, onEdit, visible, setVisible, flash }: Props): JSX.Element {
    const { assessmentDescription, assessmentMetadatas } = useContext(TcoContext);
    const { portfolio } = useContext(PortfolioContext);
    
    const defaultNewAssessmentData: CreateNewAssessment = {
        name: '',
        useDefault: 'true',
    };

    const defaultEditAssessmentData: EditAssessment = {
        id: '',
        name: '',
        overrideAssumptions: false,
        copyAssessmentId: {}
    };

    const assessmentOptions: EnumItem[] = assessmentMetadatas.map(metadata => {
        return {
            value: metadata.id,
            text: metadata.name || constants.DEFAULT_ASSESSMENT_NAME,
        };
    });

    const defaultCreateAssessmentFormMetadata: DistributeMetadataItem<CreateNewAssessment>[] = [
        {
            identifier: 'name',
            label: constants.CREATE_MODAL_ASSESSMENT_NAME_FIELD_LABEL,
            placeholder: `${portfolio.name} - ${(portfolio.tcoAssessmentIds?.length ?? 0) + 1}`,
            type: 'string',
            required: true,
            validation: {
                minLen: 1,
                maxLen: 50,
            },
        },
        {
            identifier: 'useDefault',
            label: '',
            type: 'radioGroup',
            required: true,
            enum: [
                {
                    value: 'true',
                    text: constants.USE_DEFAULT_ASSUMPTION_VALUE,
                },
                {
                    value: 'false',
                    text: constants.USE_ASSUMPTION_VALUE_FROM_ASSESSMENT,
                }
            ],
        },
        {
            identifier: 'copyAssessmentId',
            label: constants.CREATE_MODAL_ASSESSMENT_NAME_FIELD_LABEL,
            type: 'enum',
            enum: assessmentOptions,
            uiDisabled: true,
        },
    ];

    const [ createAssessmentMetadata, setCreateAssessmentMetadata ] = useState(defaultCreateAssessmentFormMetadata);
    const [ editAssessmentOverrideOptions, setEditAssessmentOverrideOptions ] = useState<SelectProps.Options>([]);
    const [ formNewAssessmentData, setNewFormAssessmentData ] = useState(defaultNewAssessmentData);
    const [ editAssessmentData, setEditAssessmentData ] = useState<EditAssessment>(defaultEditAssessmentData);
    const [ loading, setLoading ] = useState(false);
    const [ compareAssessmentsColumnDefinition, setCompareAssessmentColumnDefinition ] = useState<TableProps.ColumnDefinition<Record<string, any>>[]>([]);
    const [ compareAssessmentsAllFields, setCompareAssessmentsAllFields ] = useState<Record<string, any>[]>([]);
    const [ compareAssessmentsFieldsWithDiff, setCompareAssessmentsFieldsWithDiff ] = useState<Record<string, any>[]>([]);
    const [ compareAssessmentsDisplayFieldsWithDiff, setCompareAssessmentDisplayFieldsWithDiff ] = useState(false);
    const [ visibleColumns, setVisibleColumns ] = useState<string[]>([]);
    const [ showColumnSelectModal, setShowColumnSelectModal ] = useState(false);

    useEffect(() => {
        const metadataToUpdate = [...createAssessmentMetadata];
        const copyAssessmentIdMetadata = metadataToUpdate.find(metadata => metadata.identifier === 'copyAssessmentId');
        if (copyAssessmentIdMetadata) {
            copyAssessmentIdMetadata.enum = assessmentOptions;
        }
        if (assessmentMetadatas.length) {
            defaultNewAssessmentData.copyAssessmentId = assessmentMetadatas[0].id;
            setNewFormAssessmentData(defaultNewAssessmentData);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessmentMetadatas ]);

    useEffect(() => {
        if (formAction === FormAction.EDIT) {
            if (assessmentMetadatas?.length && assessment?.id) {
                const assesmentOverrideOptions = [...assessmentMetadatas];
                const indexOfSelectedAssessment = assesmentOverrideOptions.findIndex(overrideOption => overrideOption.id === assessment.id);
                if (indexOfSelectedAssessment >= 0) {
                    assesmentOverrideOptions.splice(indexOfSelectedAssessment, 1);
                }
                setEditAssessmentOverrideOptions(assesmentOverrideOptions.map(metadata => {
                    return {
                        value: metadata.id,
                        label: metadata.name,
                    };
                }));
                setEditAssessmentData(assessment);
            }
        }
        else if (formAction === FormAction.COMPARE) {
            loadAssessmentsToCompare();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ formAction, assessment, assessmentMetadatas ]);

    useEffect(() => {
        if (assessmentDescription) {
            mapDescriptionToFriendlyNames();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessmentDescription ]);

    const onCancel = () => {
        setNewFormAssessmentData(defaultNewAssessmentData);
        if (assessment) {
            setEditAssessmentData(assessment);
        }
        setVisible(false);
    };

    const onCreateFormUpdate = (updatedAssessment: CreateNewAssessment): void => {
        const metadataToUpdate = [...createAssessmentMetadata];
        const copyAssessmentIdMetadata = metadataToUpdate.find(metadata => metadata.identifier === 'copyAssessmentId');
        if (copyAssessmentIdMetadata) {
            copyAssessmentIdMetadata.uiDisabled = updatedAssessment.useDefault === 'true';
            setCreateAssessmentMetadata(metadataToUpdate);
        }
    };

    const onCreateFormSubmit = (newAssessment: CreateNewAssessment) => {
        onCreate(newAssessment);
        setNewFormAssessmentData(defaultNewAssessmentData);
        setVisible(false);
    };

    const onEditFromSubmit = () => {
        onEdit(editAssessmentData);
        setVisible(false);
    };

    const onOverrideCheckChange = (event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>) => {
        setEditAssessmentData({
            ...editAssessmentData,
            overrideAssumptions: event.detail.checked,
        });
    };

    const onAssessmentNameEdit = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
        setEditAssessmentData({
            ...editAssessmentData,
            name: event.detail.value,
        });
    };

    const onAssessmentOverrideSelection = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        setEditAssessmentData({
            ...editAssessmentData,
            copyAssessmentId: event.detail.selectedOption,
        });
    };

    const editContainerHeader = (
        <Checkbox checked={editAssessmentData.overrideAssumptions} onChange={onOverrideCheckChange}>
            {constants.EDIT_ASSESSMENT_OVERRIDE_ASSUMPTIONS_MESSAGE}
        </Checkbox>
    );

    const modalHeader = () => {
        switch (formAction) {
            case FormAction.ADD:
                return constants.CREATE_NEW_ASSESSMENT_MODAL_TITLE;
            case FormAction.EDIT:
                return constants.EDIT_ASSESSMENT_MODAL_TITLE;
            case FormAction.COMPARE:
                return constants.COMPARE_ASSESSMENT_MODAL_TITLE;
            default:
                return constants.DEFAULT_ASSESSMENT_MODAL_TITLE;
                break;
        }
    };

    const formatTotalFields = (compareAssessments: AssessmentToCompare[]): Record<string, string | JSX.Element>[] => {
        // Format top-level summary line items

        // AWS total
        const awsTotalLineItem: Record<string, string> = {
            label: constants.AWS_TOTAL_COST,
            template: 'assessmentTotalValue',
        };

        // Saving Percent total
        const percentageSavingLineItem: Record<string, string | JSX.Element> = {
            label: constants.SAVING_PERCENT,
            template: 'assessmentTotalValue',
        };

        // On-Premises Percent total
        const onPremisesTotalLineItem: Record<string, string> = {
            label: constants.ON_PREM_TOTAL_COST,
            template: 'assessmentTotalValue',
        };

        compareAssessments.forEach(assessment => {
            const options: FormatNumberOptions = {
                currency: assessment.outputSummary.currency,
                maximumFractionDigits: 0,
            };

            awsTotalLineItem[assessment.id] = formatCurrencyAsString(assessment.outputSummary.aws.adjustedTotal, options);
            percentageSavingLineItem[assessment.id] = formatPercentage(assessment.outputSummary.awsSavingPercent);
            onPremisesTotalLineItem[assessment.id] = formatCurrencyAsString(assessment.outputSummary.onPremises.adjustedTotal,
                options);
        });

        return [ awsTotalLineItem, percentageSavingLineItem, onPremisesTotalLineItem ];
    };

    const formatCategoryFields = (compareAssessments: AssessmentToCompare[]): Category[] => {
        const formattedCategories: Category[] = deepCopy(categories);
        compareAssessments.forEach(assessment => {
            const options: FormatNumberOptions = {
                currency: assessment.outputSummary.currency,
                maximumFractionDigits: 0,
            };
            formattedCategories.forEach(category => {
                if (category.environment) {
                    const environmentOutput = assessment.outputSummary[category.environment];
                    category[assessment.id] = formatCurrencyAsString(environmentOutput[category.id as keyof AWSOnPremisesSummary] ?? 0, options);
                }
            });
        });
        return formattedCategories;
    };

    const friendlyNameConfigurationValuesMap: Map<string, string> = new Map<string, string>();
    const friendlyNameConfigurationMap: Map<string, {label: string; properties: Record<string, {label: string;}>}> =
        new Map<string, {label: string; properties: Record<string, {label: string;}>}>();

    const formatConfiguration = (compareAssessments: AssessmentToCompare[], configurationSection: Category) => {
        const allFields: Record<string, any>[] = [configurationSection];
        let fieldsWithDifferences: Record<string, any>[] = [configurationSection];

        if (configurationSection.childCategories && Array.isArray(configurationSection.childCategories)) {
            configurationSection.childCategories.forEach(childCategory => {
                const configurationNames = Object.keys(compareAssessments[0].configuration[childCategory as keyof AssessmentConfiguration]);
                configurationNames.forEach(configurationId => {
                    const friendlyNameConfiguration = friendlyNameConfigurationMap.get(childCategory);
                    if (!friendlyNameConfiguration?.properties[configurationId]) {
                        return;
                    }
                    const item: Record<string, any> = {
                        id: configurationId,
                        label: friendlyNameConfiguration.properties[configurationId].label,
                    };
                    let previousAssessmentValue: string;
                    let hasDifference = false;
                    const valueHasFriendlyName = friendlyValueNames.hasOwnProperty(configurationId);

                    compareAssessments.forEach(assessment => {
                        const parentCategoryValue = assessment.configuration[childCategory as keyof AssessmentConfiguration];
                        type ConfigurationType = typeof parentCategoryValue;
                        let returnValue = '';
                        let value: boolean | number | string | string[] = parentCategoryValue[configurationId as keyof ConfigurationType];
                        if (value == undefined || value === '') {
                            returnValue = '-';
                        }
                        else if (isStringArray(value)) {
                            if (valueHasFriendlyName) {
                                value = (value as string[]).map(itemValue => friendlyNameConfigurationValuesMap.get(itemValue) || itemValue);
                            }
                            returnValue = value.sort().join(', ');
                        }
                        else if (typeof value === 'boolean') {
                            returnValue = value === true ? 'True' : 'False';
                        }
                        else if (typeof value === 'string' || typeof value === 'number') {
                            if (valueHasFriendlyName) {
                                returnValue = friendlyNameConfigurationValuesMap.get(value) || value;
                            }
                            else {
                                returnValue = value;
                            }
                        }
                        item[assessment.id] = returnValue;


                        if (!previousAssessmentValue) {
                            previousAssessmentValue = returnValue;
                        }
                        else if (previousAssessmentValue !== returnValue) {
                            hasDifference = true;
                        }
                    });
                    allFields.push(item);
                    if (hasDifference) {
                        fieldsWithDifferences.push(item);
                    }
                });
            });
        }
        if (fieldsWithDifferences.length === 1) {
            // empty out fieldsWithDifferences if the values are the same for configurationSection
            let assessmentValue: string;
            let configurationSectionHasDifference = false;
            compareAssessments.forEach(assessment => {
                if (!assessmentValue) {
                    assessmentValue = configurationSection[assessment.id];
                }
                else if (assessmentValue !== configurationSection[assessment.id]) {
                    configurationSectionHasDifference = true;
                }
            });

            if (!configurationSectionHasDifference) {
                fieldsWithDifferences = [];
            }
        }

        return { allFields, fieldsWithDifferences };
    };

    const mapDescriptionToFriendlyNames = () => {
        if (assessmentDescription) {
            assessmentDescription.forEach(category => {
                friendlyNameConfigurationMap.set(category.identifier, {label: category.label, properties: {}});
                category.properties.forEach(property => {
                    const categoryNameConfig = friendlyNameConfigurationMap.get(category.identifier);
                    if (categoryNameConfig) {
                        categoryNameConfig.properties[property.identifier] = { label: property.label };
                        friendlyNameConfigurationMap.set(category.identifier, categoryNameConfig);

                        if (property.type === 'enum' || property.type === 'multiValueEnum') {
                            const enumValues = property.enum || property.selections || [];
                            enumValues.forEach(propertyEnum => friendlyNameConfigurationValuesMap.set(propertyEnum.value, propertyEnum.text));
                        }
                    }
                });
            });
        }
    };

    const formatOutput = (item: Record<string, any>, id: string): JSX.Element => {
        switch (item.template) {
            case 'assessmentTotalValue':
                return (<strong>{item[id]}</strong>);
            case 'categorySection':
                return (<span id='compare-assessment-category-section'><strong>{item[id]}</strong></span>);
            default:
                return (item[id]);
        }
    };

    const loadAssessmentsToCompare = async () => {
        try {
            setLoading(true);
            mapDescriptionToFriendlyNames();
            if (portfolio?.tcoAssessmentIds && assessmentMetadatas) {
                const { assessments } = await tcoService.compareAssessments(portfolio.tcoAssessmentIds);
                const columnDefinition: TableProps.ColumnDefinition<Record<string, any>>[] = [
                    {
                        id: 'label',
                        header: '',
                        cell: item => formatOutput(item, 'label'),
                    }
                ];

                assessmentMetadatas.forEach(assessment => {
                    columnDefinition.push({
                        id: assessment.id,
                        header: assessment.name,
                        cell: item => formatOutput(item, assessment.id),
                    });
                });

                setCompareAssessmentColumnDefinition(columnDefinition);
                setVisibleColumns(columnDefinition.filter(record => !!record.id).map(record => record.id) as string[]);

                const totalFields = formatTotalFields(assessments);
                const categoryFields = formatCategoryFields(assessments);
                let allFields: Record<string, any> = [];
                let fieldsWithDifferences: Record<string, any> = [];
                categoryFields.forEach(category => {
                    const formattedFields = formatConfiguration(assessments, category);
                    allFields = allFields.concat(formattedFields.allFields);
                    fieldsWithDifferences = fieldsWithDifferences.concat(formattedFields.fieldsWithDifferences);
                });
                setCompareAssessmentsAllFields(totalFields.concat(allFields));
                setCompareAssessmentsFieldsWithDiff(totalFields.concat(fieldsWithDifferences));
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_COMPARE_ASSESSMENTS);
        } finally {
            setLoading(false);
        }
    };

    const onDisplayFieldsToggle = (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
        setCompareAssessmentDisplayFieldsWithDiff(event.detail.checked);
    };

    const onColumnSelectToggle = () => {
        setShowColumnSelectModal(!showColumnSelectModal);
    };

    const content = (): JSX.Element => {
        switch (formAction) {
            case FormAction.ADD:
                return (
                    <AWSUIFormBuilder
                        item={formNewAssessmentData}
                        metadata={createAssessmentMetadata}
                        onCancel={onCancel}
                        primaryAction={constants.CREATE_BUTTON_TEXT}
                        onUpdate={onCreateFormUpdate}
                        onSubmit={onCreateFormSubmit}
                    />
                );
            case FormAction.EDIT:
                return (
                    <Form
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={onCancel}>Cancel</Button>
                                <Button variant="primary" onClick={onEditFromSubmit}>{constants.SAVE_BUTTON_TEXT}</Button>
                            </SpaceBetween>
                        }
                    >
                        <SpaceBetween size='l'>
                            <FormField label='Assessment name *' description='Max 50 characters'>
                                <Input value={editAssessmentData.name} onChange={onAssessmentNameEdit} />
                            </FormField>
                            <Container header={editContainerHeader}>
                                <Select
                                    selectedOption={editAssessmentData.copyAssessmentId}
                                    options={editAssessmentOverrideOptions}
                                    onChange={onAssessmentOverrideSelection}
                                    disabled={!editAssessmentData.overrideAssumptions}
                                />
                            </Container>
                        </SpaceBetween>
                    </Form>
                );
            case FormAction.COMPARE:
                return (
                    <SpaceBetween size='m'>
                        <Box>
                            <Box display='inline-block'> 
                                <Toggle checked={compareAssessmentsDisplayFieldsWithDiff} onChange={onDisplayFieldsToggle}>
                                    {constants.ONLY_SHOW_DIFFERENCES}
                                </Toggle>
                            </Box>
                            <Box display='inline-block' float='right' data-testid='ColumnSelectToggle' >
                                <Button iconName='settings' onClick={onColumnSelectToggle}></Button>
                            </Box>
                        </Box>
                        <Table
                            columnDefinitions={compareAssessmentsColumnDefinition}
                            items={ compareAssessmentsDisplayFieldsWithDiff ? compareAssessmentsFieldsWithDiff : compareAssessmentsAllFields}
                            visibleColumns={visibleColumns}
                            wrapLines
                        />
                        <Modal
                            data-testid='ColumnSelectModal'
                            header={constants.SHOW_HIDE_COLUMN}
                            visible={showColumnSelectModal}
                            onDismiss={() => setShowColumnSelectModal(false)}
                            footer={
                                <Box float="right">
                                    <Button variant="primary" onClick={onColumnSelectToggle}>{constants.CLOSE}</Button>
                                </Box>
                            }
                        >
                            {
                                assessmentMetadatas.map(assessmentMetadata => {
                                    const onCheckboxChange = (checked: boolean) => {
                                        const index = visibleColumns.indexOf(assessmentMetadata.id);
                                        if (index > -1 && !checked) {
                                            setVisibleColumns(visibleColumns.filter(columnId => columnId != assessmentMetadata.id));
                                        } else if (index < 0 && checked) {
                                            setVisibleColumns([...visibleColumns, assessmentMetadata.id]);
                                        }
                                    };
                                    return (
                                        <Checkbox
                                            checked={visibleColumns.indexOf(assessmentMetadata.id) > -1}
                                            onChange={({ detail }) => onCheckboxChange(detail.checked)}>
                                            {assessmentMetadata.name}
                                        </Checkbox>
                                    );
                                })
                            }
                        </Modal>
                    </SpaceBetween>
                );
            default:
                return (<>{constants.UNSUPPORTED_MODAL_ACTION}</>);
                break;
        }
    };

    const modalSize = formAction === FormAction.COMPARE ? 'max' : 'medium';

    return (
        <Modal
            data-testid='AssessmentControls'
            header={modalHeader()}
            visible={visible}
            onDismiss={onCancel}
            size={modalSize}
        >
            {loading ? <Spinner /> : content()}
        </Modal>
    );
}

export default withFlashContext(AssessmentControlsModal);
